import type { components } from './schema';

export type ProblemDetails = components['schemas']['ProblemDetails'];
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;
  constructor(status: number, problem: ProblemDetails) {
    super(problem.detail ?? problem.title ?? `API request failed with status ${status}`);
    this.name = 'ApiError'; this.status = status; this.problem = problem;
  }
}

export type ApiFailureContext = { method: string; path: string };
export type ApiFailureHandler = (error: ApiError, context: ApiFailureContext) => void;
const failureHandlers = new Set<ApiFailureHandler>();

/** Subscribe to normalised API failures. Used by @boothiva/auth for global 401/403 handling. */
export function subscribeToApiFailures(handler: ApiFailureHandler) {
  failureHandlers.add(handler);
  return () => { failureHandlers.delete(handler); };
}

export interface ApiClientOptions { origin: string; antiforgeryToken?: string; fetch?: typeof globalThis.fetch; portal?: 'customer' | 'admin' }
export interface ApiResponse<T> { data: T; response: Response }
const unsafe = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
function stripTenantIds(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripTenantIds);
  if (!value || typeof value !== 'object' || value instanceof Date || value instanceof FormData) return value;
  return Object.fromEntries(Object.entries(value).filter(([key]) => key.toLowerCase() !== 'tenantid').map(([key, entry]) => [key, stripTenantIds(entry)]));
}

export function createApiClient(options: ApiClientOptions) {
  const origin = new URL(options.origin); const fetcher = options.fetch ?? globalThis.fetch; let antiforgeryToken = options.antiforgeryToken; const portal = options.portal;
  async function fetchAntiforgeryToken(): Promise<string> {
    const tokenResponse = await fetcher(new URL('/api/v1/auth/antiforgery', origin), { credentials: 'include', headers: portal ? { Accept: 'application/json', 'X-Boothiva-Portal': portal } : { Accept: 'application/json' } });
    if (!tokenResponse.ok) throw new ApiError(tokenResponse.status, { status: tokenResponse.status, title: 'Unable to obtain antiforgery token' });
    return (await tokenResponse.json() as { token: string }).token;
  }
  async function readProblem(response: Response): Promise<ProblemDetails> {
    const problem: ProblemDetails = { status: response.status, title: response.statusText };
    if (!response.headers.get('content-type')?.includes('json')) return problem;
    return { ...problem, ...await response.json() as ProblemDetails };
  }
  async function send(method: string, path: string, body?: unknown, requestHeaders?: HeadersInit): Promise<Response> {
    const url = new URL(path, origin); const sameOrigin = url.origin === origin.origin;
    // Held outside the attempt so a retry re-sends the same request rather than a new one: the rejected
    // attempt never reached the handler, so its idempotency key was never claimed.
    const idempotencyKey = method === 'POST' ? globalThis.crypto.randomUUID() : undefined;
    const serialisedBody = body === undefined ? undefined : JSON.stringify(stripTenantIds(body));
    async function attempt(): Promise<Response> {
      const headers = new Headers(requestHeaders);
      if (!headers.has('Accept')) headers.set('Accept', 'application/json');
      // Identifies which SPA is calling so the API can keep the admin and customer portals' session
      // cookies isolated even though both call the same API origin — deliberately explicit rather than
      // inferred from Origin/Referer, since those are browser-optional/privacy-setting-dependent on a
      // same-origin request proxied through Vite's dev server.
      if (portal) headers.set('X-Boothiva-Portal', portal);
      if (body !== undefined) headers.set('Content-Type', 'application/json');
      if (unsafe.has(method) && sameOrigin && !antiforgeryToken) antiforgeryToken = await fetchAntiforgeryToken();
      if (unsafe.has(method) && antiforgeryToken) headers.set('X-XSRF-TOKEN', antiforgeryToken);
      if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
      const init: RequestInit = { method, headers };
      if (sameOrigin) init.credentials = 'include';
      if (serialisedBody !== undefined) init.body = serialisedBody;
      return fetcher(url, init);
    }
    let response = await attempt();
    if (!response.ok) {
      let problem = await readProblem(response);
      // The token embeds the signed-in identity, so one minted before sign-in stops being accepted the
      // instant someone logs in — and likewise on logout and on entering or leaving a support session.
      // That is ordinary, not an attack, and the user should not see a failed save because of it: drop the
      // stale token and try once against a fresh one. Only once, so a genuinely rejected request still
      // surfaces rather than looping.
      if (response.status === 400 && (problem as { code?: string }).code === 'antiforgery_invalid') {
        antiforgeryToken = undefined;
        response = await attempt();
        if (!response.ok) problem = await readProblem(response);
      }
      if (!response.ok) {
        const error = new ApiError(response.status, problem);
        failureHandlers.forEach((handler) => handler(error, { method, path: url.pathname }));
        throw error;
      }
    }
    return response;
  }
  async function requestWithResponse<T>(method: string, path: string, body?: unknown, headers?: HeadersInit): Promise<ApiResponse<T>> {
    const response = await send(method, path, body, headers);
    const data = response.status === 204 || !response.headers.get('content-type')?.includes('json')
      ? undefined as T
      : await response.json() as T;
    return { data, response };
  }
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    return (await requestWithResponse<T>(method, path, body)).data;
  }
  return {
    setAntiforgeryToken(token: string) { antiforgeryToken = token; },
    get: <T>(path: string) => request<T>('GET', path),
    getWithResponse: <T>(path: string) => requestWithResponse<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    postBlob: async (path: string, body?: unknown) => (await send('POST', path, body)).blob(),
    /** A binary GET through the client, so the portal header and session cookie go with it (an <img> sends neither header). */
    getBlob: async (path: string) => (await send('GET', path)).blob(),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
    putWithResponse: <T>(path: string, body: unknown, headers?: HeadersInit) => requestWithResponse<T>('PUT', path, body, headers),
    delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body)
  };
}

type ConfiguredApiClient = ReturnType<typeof createApiClient>;
let defaultClient: ConfiguredApiClient | undefined;
function configuredClient(): ConfiguredApiClient {
  if (!defaultClient) throw new Error('apiClient must be configured with its API origin before use.');
  return defaultClient;
}

export const apiClient = {
  configure(options: ApiClientOptions) { defaultClient = createApiClient(options); },
  setAntiforgeryToken(token: string) { configuredClient().setAntiforgeryToken(token); },
  get: <T>(path: string) => configuredClient().get<T>(path),
  getWithResponse: <T>(path: string) => configuredClient().getWithResponse<T>(path),
  post: <T>(path: string, body?: unknown) => configuredClient().post<T>(path, body),
  postBlob: (path: string, body?: unknown) => configuredClient().postBlob(path, body),
  getBlob: (path: string) => configuredClient().getBlob(path),
  put: <T>(path: string, body?: unknown) => configuredClient().put<T>(path, body),
  putWithResponse: <T>(path: string, body: unknown, headers?: HeadersInit) => configuredClient().putWithResponse<T>(path, body, headers),
  delete: <T>(path: string, body?: unknown) => configuredClient().delete<T>(path, body)
};
